import Events from '@/shared/ipcUtils/Events'
import { Capacitor } from '@capacitor/core'
import mixpanel from 'mixpanel-browser'

// How an event update was made. 'dialog' is an edit through the form; the other
// three are direct grid manipulations (drag/resize) or an automated defrag move.
export type EventUpdateMethod = 'drag' | 'resize' | 'dialog' | 'defrag'
// The origins a pending move/resize can have. A pending move is never a 'dialog'
// edit, so it is the update methods minus 'dialog'.
export type EventMoveMethod = Exclude<EventUpdateMethod, 'dialog'>

// Detect platform
const isMobile = Capacitor.isNativePlatform()
const isDesktop = !isMobile && typeof window !== 'undefined' && window.mainApi
const isWeb = !isMobile && !isDesktop

// Mobile-specific state
let mixpanelInitialized = false

// Initialize Mixpanel for mobile
const initializeMobileAnalytics = () => {
  if (mixpanelInitialized || (!isMobile && !isWeb)) return

  const token = import.meta.env.VITE_MIXPANEL_TOKEN
  if (token) {
    mixpanel.init(token, {
      track_pageview: false,
      persistence: 'localStorage'
    })
    mixpanelInitialized = true
  } else {
    console.warn('Mixpanel token not found. Analytics disabled.')
  }
}

// Initialize on mobile immediately
if (isMobile) {
  initializeMobileAnalytics()
}
if (isWeb) {
  initializeMobileAnalytics()
}

export const useAnalytics = () => {
  const trackEvent = (event: string, properties?: Record<string, any>) => {
    if (isDesktop) {
      // Desktop: Use IPC to main process
      window.mainApi.send(Events.Analytics.TRACK_EVENT, {
        eventName: event,
        properties: { ...properties }
      })
    } else if ((isMobile || isWeb) && mixpanelInitialized) {
      // Mobile/Web: Use browser SDK directly
      const platform = isWeb ? 'web' : Capacitor.getPlatform()
      mixpanel.track(event, {
        ...properties,
        platform,
        app_version: import.meta.env.VITE_APP_VERSION || '1.0.0'
      })
    } else {
      console.warn('No analytics API found')
    }
  }

  const identifyUser = async (email: string, name?: string): Promise<any> => {
    if (isDesktop) {
      // Desktop: Use IPC to main process
      return window.mainApi.invoke(Events.Analytics.IDENTIFY_USER, email, name)
    } else if ((isMobile || isWeb) && mixpanelInitialized) {
      // Mobile/Web: Identify user directly
      const platform = isWeb ? 'web' : Capacitor.getPlatform()

      mixpanel.identify(email)
      mixpanel.people.set({
        $name: name,
        $email: email,
        platform,
        app_version: import.meta.env.VITE_APP_VERSION || '1.0.0'
      })

      return { success: true }
    } else {
      console.warn('Analytics not available')
      return { success: false, error: 'Analytics not initialized' }
    }
  }

  const resetUser = () => {
    if ((isMobile || isWeb) && mixpanelInitialized) {
      mixpanel.reset()
    }
  }

  const trackLogin = () => {
    trackEvent('Login')
  }

  const trackLogout = (email: string) => {
    trackEvent('Logout', { email })
    resetUser()
  }

  const trackNavigation = (from: string, to: string) => {
    trackEvent('Navigation', { from, to })
  }

  const trackPreferencesOpened = () => {
    trackEvent('Preferences Opened')
  }

  const trackCmdKOpened = () => {
    trackEvent('CmdK Opened')
  }

  const trackNoteViewed = (noteId: string) => {
    trackEvent('Note Viewed', { noteId })
  }

  const trackNoteCreated = () => {
    trackEvent('Note Created')
  }

  const trackNoteDeleted = (noteId: string) => {
    trackEvent('Note Deleted', { noteId })
  }

  const trackEventCreated = (eventId?: string) => {
    trackEvent('Event Created', eventId ? { eventId } : undefined)
  }

  // `method` says HOW the change was made: a direct grid manipulation ('drag' /
  // 'resize'), the edit dialog ('dialog'), or an automated defrag move
  // ('defrag'). Defaults to 'dialog' so existing call sites keep their meaning.
  const trackEventUpdated = (eventId: string, method: EventUpdateMethod = 'dialog') => {
    trackEvent('Event Updated', { eventId, method })
  }

  const trackEventCancelled = (eventId: string) => {
    trackEvent('Event Cancelled', { eventId })
  }

  const trackEventViewed = (eventId: string) => {
    trackEvent('Event Viewed', { eventId })
  }

  const trackRsvp = (eventId: string, status: string) => {
    trackEvent('Rsvp', { eventId, status })
  }

  // App opened — the mobile/web analog of desktop's 'App Launched' (which fires
  // in the main process). Sent on cold start and on resume from background so
  // engagement/retention has a session-start signal on every platform.
  const trackAppOpened = (properties?: Record<string, any>) => {
    trackEvent('App Opened', properties)
  }

  // A search was actually run (non-empty query), not just the search UI opened
  // ('CmdK Opened' covers that).
  const trackSearchPerformed = (properties?: Record<string, any>) => {
    trackEvent('Search Performed', properties)
  }

  // The subscription/manage surface was viewed. `source` says which one
  // (e.g. 'mobile_update_screen', 'desktop_account_tab').
  const trackSubscriptionPageViewed = (properties?: Record<string, any>) => {
    trackEvent('subscription_page_viewed', properties)
  }

  // The user clicked to manage/cancel/change their subscription (opens the
  // Stripe portal or the App/Play store). Centralizes the event name so the
  // desktop call sites don't hand-build divergent property bags.
  const trackSubscriptionManageClicked = (properties?: Record<string, any>) => {
    trackEvent('subscription_manage_clicked', properties)
  }

  // A paywall/upsell surface became visible. `source` names the surface so the
  // purchase funnel (impression → attempt → complete) is computable. Uses the
  // same `source` key as the other domain events for one uniform breakdown.
  const trackPaywallViewed = (source: string, properties?: Record<string, any>) => {
    trackEvent('paywall_viewed', { source, ...properties })
  }

  // A move/resize was started (the confirm dialog opened) but the user backed
  // out. Pairs with the `Event Updated` {method} that fires on confirm, so an
  // abandon rate per method is computable — these were invisible before. Uses
  // the same method vocabulary as the confirm so the two events stay pairable.
  const trackEventMoveCancelled = (method: EventMoveMethod) => {
    trackEvent('Event Move Cancelled', { method })
  }

  // A calendar account (Google/Microsoft) was connected for the first time —
  // distinct from a reconnect.
  const trackCalendarAccountConnected = (provider: string, properties?: Record<string, any>) => {
    trackEvent('calendar_account_connected', { provider, ...properties })
  }

  return {
    identifyUser,
    trackEvent,
    trackLogin,
    trackLogout,
    trackNavigation,
    trackPreferencesOpened,
    trackCmdKOpened,
    trackNoteViewed,
    trackNoteCreated,
    trackNoteDeleted,
    trackEventCreated,
    trackEventUpdated,
    trackEventCancelled,
    trackEventViewed,
    trackEventMoveCancelled,
    trackRsvp,
    trackAppOpened,
    trackSearchPerformed,
    trackSubscriptionPageViewed,
    trackSubscriptionManageClicked,
    trackPaywallViewed,
    trackCalendarAccountConnected
  }
}
